import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../api';
import { Mail, ArrowLeft, CheckCircle, Zap } from 'lucide-react';

/**
 * ForgotPasswordPage — Performance Club dark theme.
 *
 * Centered single-column layout (no split hero) because this is a
 * quick transactional page, not a marketing surface. Same dark
 * surface + lime accents as /, /free, /about, /guide, /login, /register.
 */
export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-surface)' }} data-testid="forgot-password-success">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 justify-center mb-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/30 bg-primary-200/10 font-display text-lg uppercase text-primary-100">GG</span>
            <p className="font-display text-lg uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
          </Link>
          <div className="editorial-panel p-8 text-center">
            <div className="w-16 h-16 rounded-full border border-primary-200/30 bg-primary-200/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-primary-100" />
            </div>
            <span className="hero-kicker">Sent</span>
            <h2 className="mt-3 editorial-title text-3xl text-[var(--color-text)]">Check your email.</h2>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              If an account exists for <strong className="text-[var(--color-text)]">{email}</strong>, we have sent password reset instructions.
            </p>
            <p className="mt-4 text-xs text-[var(--color-text-muted)]">
              Didn’t receive the email? Check your spam folder or try again.
            </p>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => setSubmitted(false)}
                className="w-full py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] font-medium text-[var(--color-text)] hover:bg-white/[0.06] transition-colors"
              >
                Try another email
              </button>
              <Link
                to="/login"
                className="block w-full py-3 bg-gradient-to-r from-primary-500 to-primary-300 text-surface rounded-xl font-semibold text-center hover:from-primary-300 hover:to-primary-200 transition-all shadow-lg shadow-primary-500/20"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-surface)' }} data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-6">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/30 bg-primary-200/10 font-display text-lg uppercase text-primary-100">GG</span>
          <p className="font-display text-lg uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
        </Link>
        <div className="editorial-panel p-8 space-y-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <ArrowLeft className="w-3 h-3" /> Back to login
          </Link>

          <div>
            <span className="hero-kicker">Reset password</span>
            <h1 className="mt-3 editorial-title text-3xl text-[var(--color-text)]">Forgot your password?</h1>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              No worries. Enter your email and we will send you reset instructions.
            </p>
          </div>

          {error && (
            <div className="border border-red-500/30 bg-red-500/10 text-red-200 p-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary-500 to-primary-300 text-surface font-semibold py-3.5 rounded-xl hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20"
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-surface border-t-transparent rounded-full animate-spin" />
              ) : (
                'Send reset instructions'
              )}
            </button>
          </form>

          <p className="text-center text-sm text-[var(--color-text-muted)]">
            Remember your password?{' '}
            <Link to="/login" className="text-primary-100 font-semibold hover:text-primary-200">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
